import { FileText, Download } from 'lucide-react'
import type { DownloadableAsset } from '../../types'

interface ProductDocumentsProps {
  downloadable_assets: DownloadableAsset[]
  datasheetUrls?: string[]
  manualUrls?: string[]
  brochureUrls?: string[]
}

const TYPE_LABELS: Record<DownloadableAsset['type'], string> = {
  datasheet: 'Datasheets',
  manual: 'Handleidingen',
  brochure: 'Brochures',
  certificate: 'Certificaten',
  cad: 'CAD-bestanden',
  software: 'Software',
  other: 'Overige documenten',
}

const TYPE_ORDER: DownloadableAsset['type'][] = ['datasheet', 'manual', 'brochure', 'certificate', 'cad', 'software', 'other']

function filenameFromUrl(url: string): string | null {
  try {
    const path = new URL(url, 'http://x').pathname
    const last = path.split('/').filter(Boolean).pop()
    if (!last) return null
    // Strip extension and decode percent-escapes for human-readable filenames.
    const decoded = decodeURIComponent(last).replace(/\.[a-z0-9]+$/i, '')
    return decoded || null
  } catch {
    return null
  }
}

function urlsToAssets(urls: string[], type: DownloadableAsset['type'], baseTitle: string): DownloadableAsset[] {
  return urls.filter(Boolean).map(url => {
    const filename = filenameFromUrl(url)
    return {
      url,
      type,
      title: filename ?? baseTitle,
      language: 'en',
      file_type: 'PDF',
    }
  })
}

function dedupeAssets(assets: DownloadableAsset[]): DownloadableAsset[] {
  const seen = new Set<string>()
  return assets.filter(a => {
    if (seen.has(a.url)) return false
    seen.add(a.url)
    return true
  })
}

export default function ProductDocuments({ downloadable_assets, datasheetUrls = [], manualUrls = [], brochureUrls = [] }: ProductDocumentsProps) {
  // Merge URL-only arrays with structured assets; URL-only entries come first so they appear at the top of their group
  const syntheticDatasheets = urlsToAssets(datasheetUrls, 'datasheet', 'B&R Datasheet')
  const syntheticManuals = urlsToAssets(manualUrls, 'manual', 'B&R Manual')
  const syntheticBrochures = urlsToAssets(brochureUrls, 'brochure', 'B&R Brochure')

  const allAssets = dedupeAssets([...syntheticDatasheets, ...syntheticManuals, ...syntheticBrochures, ...downloadable_assets])

  if (allAssets.length === 0) {
    return (
      <div className="py-8 text-center text-neutral-400">
        <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Geen documenten beschikbaar voor dit product.</p>
      </div>
    )
  }

  // Group by type
  const groups: Partial<Record<DownloadableAsset['type'], DownloadableAsset[]>> = {}
  for (const asset of allAssets) {
    if (!groups[asset.type]) groups[asset.type] = []
    groups[asset.type]!.push(asset)
  }

  return (
    <div className="flex flex-col gap-6" data-testid="product-documents">
      {TYPE_ORDER.filter(t => groups[t] && groups[t]!.length > 0).map(type => (
        <div key={type}>
          <h3 className="text-sm font-semibold text-neutral-700 mb-3">{TYPE_LABELS[type]}</h3>
          <div className="flex flex-col gap-2">
            {groups[type]!.map((asset, i) => (
              <a
                key={i}
                href={asset.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:border-primary/30 hover:bg-gcg-tint/30 transition-colors group"
              >
                <FileText className="h-5 w-5 text-neutral-400 group-hover:text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">{asset.title}</p>
                  {(asset.language || asset.file_type) && (
                    <p className="text-xs text-neutral-400">
                      {[asset.language, asset.file_type?.toUpperCase()].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                <Download className="h-4 w-4 text-neutral-300 group-hover:text-primary shrink-0" />
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
